/**
 * store/pult.ts — the pult's own slice: which state it is in, the window onto each board, what
 * its screen shows, and where its knobs and faders stand (task 54).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Лупа и увеличение».
 *
 * - **The state** (`view`): `play` — every finger goes to a control, one finger never zooms;
 *   `zoom` — after the magnifier, the controls sleep and fingers move the picture. Every opening
 *   of the pult starts in `play`: a pult that opens asleep looks broken.
 * - **The windows** (`windows`), one per orientation (`lib/pult-zoom.ts`), are remembered on the
 *   phone ({@link PULT_VIEW_KEY}) as the record and the tempo are (`store/deck.ts`). A value nobody
 *   wrote, a broken one and a phone that refuses storage all give the whole board, without an
 *   error. They are written when a finger leaves the glass, not on every move of it.
 * - **The screen** (`screen`) shows the queue, the catalogue or the two decks; `chosen` is the
 *   track picked on it, which LOAD lays on a deck (task 55).
 * - **AUTO** (`auto`, task 56): the queue goes on by itself, remembered on the phone
 *   ({@link PULT_AUTO_KEY}); on by default — the player as it always was.
 * - **The hand** (`held`, task 56): the controls a finger is on — the automat leaves them alone.
 * - **The effect** (`effect`, task 58): which of the four keys is lit — one for both decks, ECHO at
 *   every launch, as the device's at power-on; not remembered on the phone. **PRESET** (`preset`): the automat's way with the
 *   effects (`lib/pult-presets.ts`), remembered on the phone ({@link PULT_PRESET_KEY}); `clean` by
 *   default — a transition as it always was.
 * - **The pads** (`pads`, task 60): each deck's pad mode and the page PARAMETER turned to
 *   (`lib/pult-pads.ts`). HOT CUE on every launch, as the device's at power-on; not remembered on
 *   the phone. A deck's mode and page are its own, as on the device.
 * - **The controls** (`values`): where every knob, fader and paddle stands, 0…1 by the control's id
 *   (`lib/pult-layout.ts`). A knob: 0 is all the way left, 0,5 straight up. A fader: the share of
 *   its slot from the top, or from the left. A paddle: 0 down, 1 up. In task 54 they move under
 *   the finger and nothing sounds; tasks 55–58 read them for the sound. The tempo faders are not
 *   here since task 55: they are the decks' own tempos (`store/deck.ts`).
 */

import { create } from 'zustand';

import type { TrackItem } from '../lib/api';
import type { PultEffect } from '../lib/pult-effects';
import type { DeckSide, PadMode, PultOrientation, SCREEN_VIEWS } from '../lib/pult-layout';
import { turnPage } from '../lib/pult-pads';
import type { PultPreset } from '../lib/pult-presets';
import { WHOLE, asWindow } from '../lib/pult-zoom';
import type { PultWindow } from '../lib/pult-zoom';

/** Where the windows are remembered. */
export const PULT_VIEW_KEY = 'fdm:pult-view';

/** Task 56: where AUTO is remembered. */
export const PULT_AUTO_KEY = 'fdm:pult-auto';

/** Task 58: where PRESET is remembered. */
export const PULT_PRESET_KEY = 'fdm:pult-preset';

export type PultView = 'play' | 'zoom';

/** What the screen in the middle of the pult shows: the keys QUEUE, LIBRARY, VIEW. */
export type PultScreenView = (typeof SCREEN_VIEWS)[number][0];

/** Task 60: a deck's pads — the mode chosen, and the page of it PARAMETER turned to. */
export interface PultPads {
  readonly mode: PadMode;
  readonly page: number;
}

export interface PultState {
  readonly view: PultView;
  readonly windows: Readonly<Record<PultOrientation, PultWindow>>;
  readonly screen: PultScreenView;
  /** The track chosen on the screen, for LOAD. */
  readonly chosen: TrackItem | undefined;
  readonly values: Readonly<Record<string, number>>;
  /** Task 56: the controls a finger is on now — the automat writes none of them. */
  readonly held: ReadonlySet<string>;
  /** Task 56: AUTO — the queue goes on by itself, one track into the next. */
  readonly auto: boolean;
  /** Task 58: the effect the four keys chose. */
  readonly effect: PultEffect;
  /** Task 58: PRESET. */
  readonly preset: PultPreset;
  /** Task 60: each deck's pads. */
  readonly pads: Readonly<Record<DeckSide, PultPads>>;
}

/**
 * Where the controls stand on a pult nobody has touched. Task 56: the faders up, the crossfader at
 * deck A's side — the deck a launch plays on — and every knob in the middle: LEVEL at 0 dB, the
 * tone flat, the filter off. The player then sounds as it did before the mixer.
 */
const PULT_DEFAULTS: Readonly<Record<string, number>> = Object.freeze({
  'A.fader': 0,
  'B.fader': 0,
  xfader: 0,
  'A.fx': 0,
  'B.fx': 0,
});

/** A knob, fader or paddle with no default of its own stands in the middle. */
export function valueOf(values: Readonly<Record<string, number>>, id: string): number {
  return values[id] ?? PULT_DEFAULTS[id] ?? 0.5;
}

function rememberedWindows(): Record<PultOrientation, PultWindow> {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(PULT_VIEW_KEY) ?? 'null');
    const stored = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
    return { land: asWindow(stored.land) ?? WHOLE, port: asWindow(stored.port) ?? WHOLE };
  } catch {
    return { land: WHOLE, port: WHOLE };
  }
}

/** AUTO as the phone remembers it: on, unless it was turned off. */
function rememberedAuto(): boolean {
  try {
    return localStorage.getItem(PULT_AUTO_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** PRESET as the phone remembers it: `clean`, unless DUB OUT was chosen. */
function rememberedPreset(): PultPreset {
  try {
    return localStorage.getItem(PULT_PRESET_KEY) === 'dubout' ? 'dubout' : 'clean';
  } catch {
    return 'clean';
  }
}

/** Task 60: the pads as the device comes up — HOT CUE, its first page. */
const POWER_ON_PADS: PultPads = Object.freeze({ mode: 'hotcue', page: 0 });

function fresh(): PultState {
  return {
    view: 'play',
    windows: rememberedWindows(),
    screen: 'queue',
    chosen: undefined,
    values: {},
    held: new Set(),
    auto: rememberedAuto(),
    effect: 'echo',
    preset: rememberedPreset(),
    pads: { A: POWER_ON_PADS, B: POWER_ON_PADS },
  };
}

export const usePult = create<PultState>(fresh);

/** The magnifier: `play` ↔ `zoom`. */
export function toggleZoom(): void {
  usePult.setState((s) => ({ view: s.view === 'play' ? 'zoom' : 'play' }));
}

/** Back to `play` — the pult closing, for one. */
export function stopZoom(): void {
  usePult.setState({ view: 'play' });
}

/** The fingers moved the picture. Remembered by {@link keepWindows}. */
export function setWindow(orientation: PultOrientation, window: PultWindow): void {
  usePult.setState((s) => ({ windows: { ...s.windows, [orientation]: window } }));
}

/** The windows onto the phone, as they are now. */
export function keepWindows(): void {
  try {
    localStorage.setItem(PULT_VIEW_KEY, JSON.stringify(usePult.getState().windows));
  } catch {
    // Remembered for this launch only.
  }
}

/** A double tap on the magnifier: the whole board again, and remembered so. */
export function wholeWindow(orientation: PultOrientation): void {
  setWindow(orientation, WHOLE);
  keepWindows();
}

export function showOnScreen(screen: PultScreenView): void {
  usePult.setState({ screen });
}

export function chooseTrack(track: TrackItem | undefined): void {
  usePult.setState({ chosen: track });
}

/** A knob, fader or paddle moved to `value`, kept within 0…1. */
export function setValue(id: string, value: number): void {
  const within = Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.5;
  usePult.setState((s) =>
    s.values[id] === within ? s : { values: { ...s.values, [id]: within } },
  );
}

/** Task 56: a finger landed on control `id`, or left it. */
export function setHeld(id: string, on: boolean): void {
  const held = usePult.getState().held;
  if (held.has(id) === on) return;
  const next = new Set(held);
  if (on) next.add(id);
  else next.delete(id);
  usePult.setState({ held: next });
}

/** Task 56: AUTO pressed. Remembered on the phone. */
export function toggleAuto(): void {
  const auto = !usePult.getState().auto;
  usePult.setState({ auto });
  try {
    localStorage.setItem(PULT_AUTO_KEY, auto ? 'on' : 'off');
  } catch {
    // Remembered for this launch only.
  }
}

/** Task 58: one of the four effect keys. */
export function chooseEffect(effect: PultEffect): void {
  if (usePult.getState().effect !== effect) usePult.setState({ effect });
}

/** Task 58: PRESET pressed — `clean` ↔ DUB OUT. Remembered on the phone. */
export function togglePreset(): void {
  const preset: PultPreset = usePult.getState().preset === 'dubout' ? 'clean' : 'dubout';
  usePult.setState({ preset });
  try {
    localStorage.setItem(PULT_PRESET_KEY, preset);
  } catch {
    // Remembered for this launch only.
  }
}

/** Task 60: a pad mode key of deck `side` — that mode, on its first page. */
export function choosePadMode(side: DeckSide, mode: PadMode): void {
  if (usePult.getState().pads[side].mode === mode) return;
  usePult.setState((s) => ({ pads: { ...s.pads, [side]: { mode, page: 0 } } }));
}

/** Task 60: PARAMETER of deck `side`, one page back (`-1`) or on (`1`), within its mode's pages. */
export function turnPadPage(side: DeckSide, direction: -1 | 1): void {
  const { mode, page } = usePult.getState().pads[side];
  const next = turnPage(mode, page, direction);
  if (next === page) return;
  usePult.setState((s) => ({ pads: { ...s.pads, [side]: { mode, page: next } } }));
}

/** Task 56: every finger gone — the pult closed under one: nothing stays held. */
export function releaseAll(): void {
  if (usePult.getState().held.size > 0) usePult.setState({ held: new Set() });
}

/** A launch again, for tests: the windows, AUTO and PRESET as the phone remembers them, nothing else. */
export function resetPult(): void {
  usePult.setState(fresh(), true);
}
